/**
 * Fitting components into a hull.
 *
 * `geometry/compartments.ts` already answers "which enclosed spaces does this
 * line graph have, and which compartment is this *point* in". That is enough
 * for damage and exposure, where a component is a thing at a location. It is
 * not enough for an editor, where a component is a shape you are trying to fit
 * somewhere and the whole question is what it collides with.
 *
 * So this adds the missing dimension: components have real outlines, taken from
 * their models, turned in eighths of a turn, and tested exactly rather than
 * approximately.
 *
 * ── Boxes ───────────────────────────────────────────────────────────────────
 *
 * A component's outline is a rectangle. It used to be the convex hull of the
 * mesh, which is wrong for anything concave: a hull bridges the hollows, so the
 * turret and the thruster ended up with collision shapes matching neither the
 * model nor a box. A rectangle is convex, predictable, and what a collision
 * volume is meant to be — and convexity is what keeps the separating-axis test
 * exact at any angle, with no tolerance fudging and no special case for
 * diagonals.
 */
import { COMPONENT_SPECS } from '../components.js';
import { hullLines } from '../geometry/compartments.js';
import { MODEL_BOUNDS } from './footprints.generated.js';
import { EPSILON, indexJoints, } from '../geometry/types.js';
/**
 * World units per editor grid cell.
 *
 * A snapping constraint on where line ends may land, *not* a unit of its own.
 * Joint positions, line lengths, footprints and compartment areas are all in
 * world units.
 */
export const GRID = 0.25;
/** Components turn in eighths of a turn. */
export const ROTATION_STEP = Math.PI / 4;
/**
 * How much larger every component is drawn than its model.
 *
 * The models were authored against a hull that turned out much bigger than
 * them: a small ship carrying nothing but the essentials filled well under a
 * quarter of a small pad, which made the parts read as doll's-house fittings.
 * Scaling here rather than re-exporting keeps one number to turn, and applies
 * to the collision bounds as well as the mesh so what you see is what fits.
 */
export const COMPONENT_SCALE = 1.8;
/**
 * How thick a ship stands, in the same units its components are drawn at.
 *
 * A hull has to be deep enough to hold the things inside it, so this scales
 * with the components rather than being an independent number — otherwise
 * every change to `COMPONENT_SCALE` silently leaves the parts poking through
 * the deck or rattling around in a hull far too tall for them.
 *
 * Still flat rather than derived from enclosed area: the design doc ties depth
 * to surface area, but that curve wants tuning against ships that exist.
 */
export const SHIP_DEPTH = 2 * COMPONENT_SCALE;
/**
 * How much of an edge component sits *in* the hull.
 *
 * Only this much is tested. A thruster's bell and a turret's barrel are outside
 * the plating on purpose, and testing their full length would reject every
 * legal mounting.
 */
const MOUNT_DEPTH = 0.3 * COMPONENT_SCALE;
/**
 * Stand-in sizes for parts with no model yet, at model scale.
 *
 * Sized against the parts that do exist — a pilot seat's model is 1 x 2 — so
 * they are scaled up by exactly the same factor as everything else.
 */
const PLACEHOLDER_SIZES = {
    reactor: { x: 1.5, y: 1.5 },
    shield: { x: 1, y: 1 },
    scanner: { x: 0.75, y: 0.75 },
    vault: { x: 1.5, y: 1 },
    collector: { x: 0.6, y: 0.6 },
};
function boxBounds(size) {
    return {
        x: { min: -size.x / 2, max: size.x / 2 },
        y: { min: -size.y / 2, max: size.y / 2 },
        up: { min: 0, max: 0.5 },
    };
}
/**
 * A component's full extent about its model origin, in world units.
 *
 * `deployed` picks the extended model for anything with two states — landing
 * gear stowed is a blister, and gear down is a metre of leg, which is the
 * difference between a hull sitting on the pad and standing over it.
 */
export function boundsOf(type, deployed = false) {
    const spec = COMPONENT_SPECS[type];
    const name = (deployed && spec.modelDeployed) || spec.model;
    const model = name ? MODEL_BOUNDS[name] : undefined;
    const raw = model ?? boxBounds(PLACEHOLDER_SIZES[type] ?? { x: 1, y: 1 });
    return scaleBounds(raw, drawScaleOf(type));
}
/** Total scale a component is drawn at: the global lift times its own tier. */
export function drawScaleOf(type) {
    return COMPONENT_SCALE * (COMPONENT_SPECS[type].modelScale ?? 1);
}
function scaleBounds(bounds, by) {
    const scale = (extent) => ({ min: extent.min * by, max: extent.max * by });
    return { x: scale(bounds.x), y: scale(bounds.y), up: scale(bounds.up) };
}
/** Which model to draw, given whether the part is deployed. */
export function modelFor(type, deployed = false) {
    const spec = COMPONENT_SPECS[type];
    return (deployed && spec.modelDeployed) || spec.model;
}
/**
 * Which way an edge component points away from the hull.
 *
 * Derived rather than declared: the protruding end is whichever reaches further
 * from the mounting origin, and that is a fact about the mesh. The two edge
 * models were authored pointing opposite ways, so asking the geometry is more
 * reliable than agreeing a convention and hoping it is kept.
 */
export function outboardSign(type) {
    const { y } = boundsOf(type);
    return Math.abs(y.min) > Math.abs(y.max) ? -1 : 1;
}
/**
 * The rectangle actually tested, about the model's origin.
 *
 * Internal parts use their whole extent. Edge parts use only a shallow block
 * around the mounting point, since the rest is meant to be outside the plating.
 */
export function footprintRect(type) {
    const bounds = boundsOf(type);
    if (COMPONENT_SPECS[type].mounting !== 'edge')
        return { x: bounds.x, y: bounds.y };
    return { x: bounds.x, y: { min: -MOUNT_DEPTH / 2, max: MOUNT_DEPTH / 2 } };
}
/** A placed component's outline in ship coordinates, rotated and positioned. */
export function componentOutline(component) {
    const rect = footprintRect(component.type);
    const angle = (component.facing ?? 0) * ROTATION_STEP;
    const [cos, sin] = [Math.cos(angle), Math.sin(angle)];
    return [
        { x: rect.x.min, y: rect.y.min },
        { x: rect.x.max, y: rect.y.min },
        { x: rect.x.max, y: rect.y.max },
        { x: rect.x.min, y: rect.y.max },
    ].map((p) => ({
        x: component.position.x + p.x * cos - p.y * sin,
        y: component.position.y + p.x * sin + p.y * cos,
    }));
}
// ─── Convex tests ────────────────────────────────────────────────────────────
function axes(polygon) {
    const out = [];
    for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        out.push({ x: -(b.y - a.y), y: b.x - a.x });
    }
    return out;
}
function project(polygon, axis) {
    let min = Infinity;
    let max = -Infinity;
    for (const p of polygon) {
        const value = p.x * axis.x + p.y * axis.y;
        min = Math.min(min, value);
        max = Math.max(max, value);
    }
    return { min, max };
}
/**
 * Do two convex shapes genuinely interpenetrate?
 *
 * Touching does not count. Components sitting flush against each other and
 * flush against a wall is tight engineering, not an error.
 */
export function convexOverlap(a, b) {
    for (const axis of [...axes(a), ...axes(b)]) {
        const scale = Math.hypot(axis.x, axis.y);
        if (scale < EPSILON)
            continue;
        const pa = project(a, axis);
        const pb = project(b, axis);
        if (pa.max - pb.min < EPSILON * scale)
            return false;
        if (pb.max - pa.min < EPSILON * scale)
            return false;
    }
    return true;
}
function segmentOf(line, graph) {
    const joints = indexJoints(graph);
    const a = joints.get(line.a);
    const b = joints.get(line.b);
    return a && b ? [{ x: a.x, y: a.y }, { x: b.x, y: b.y }] : null;
}
function pointInPolygon(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        if (a.y > point.y === b.y > point.y)
            continue;
        const crossing = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
        if (point.x < crossing)
            inside = !inside;
    }
    return inside;
}
/** Shortest distance from a point to a segment. */
function distanceToSegment(point, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    if (lengthSquared < EPSILON)
        return Math.hypot(point.x - a.x, point.y - a.y);
    const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
    return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}
export function compartmentPolygon(compartment, graph) {
    const joints = indexJoints(graph);
    const points = [];
    for (const id of compartment.jointIds) {
        const joint = joints.get(id);
        if (joint)
            points.push({ x: joint.x, y: joint.y });
    }
    return points;
}
/** How close an edge component's mount must sit to a line to count as on it. */
const MOUNT_TOLERANCE = MOUNT_DEPTH;
/**
 * What is wrong with this placement, if anything.
 *
 * Reports rather than forbids. Dropping parts into open space and drawing the
 * hull around them afterwards is a normal way to build, so the editor has to
 * allow it and say so — and refuse only to let the ship *leave*.
 */
export function checkPlacement(candidate, graph, compartments, 
/**
 * The ship's outer skin, if the caller already has it.
 *
 * Optional because most callers check one component and would rather not
 * think about it. The editor checks every component on every edit, so it
 * passes the set in rather than retracing the whole graph per part.
 */
skin = hullLines(graph)) {
    const outline = componentOutline(candidate);
    const spec = COMPONENT_SPECS[candidate.type];
    const faults = [];
    const blockedBy = [];
    // Smallest containing compartment wins, matching how `placeComponents`
    // assigns ownership — a part is held by the walls immediately around it.
    let home = null;
    for (const compartment of compartments) {
        const polygon = compartmentPolygon(compartment, graph);
        if (polygon.length < 3)
            continue;
        if (!pointInPolygon(candidate.position, polygon))
            continue;
        if (!home || compartment.area < home.area)
            home = compartment;
    }
    let mountedOn = null;
    if (spec.mounting === 'edge') {
        mountedOn = mountLineFor(candidate, graph);
        if (!mountedOn)
            faults.push('notOnEdge');
    }
    else {
        if (!home)
            faults.push('outsideHull');
        // Lines are walls, and an internal part may not be built through one.
        for (const line of graph.lines) {
            const segment = segmentOf(line, graph);
            if (!segment || !convexOverlap(outline, segment))
                continue;
            faults.push('crossesLine');
            blockedBy.push(line.id);
        }
    }
    for (const other of graph.components) {
        if (other.id === candidate.id)
            continue;
        if (!convexOverlap(outline, componentOutline(other)))
            continue;
        faults.push('overlapsComponent');
        blockedBy.push(other.id);
    }
    /*
     * Mounted on a wall that faces space, so it can actually fire or push.
     *
     * ── This asked the wrong question until now ────────────────────────────────
     *
     * It was `compartments.some(c => c.exposed && c.lineIds.includes(mountedOn))`
     * — "does some room that touches the outside have this wall". Nearly every
     * wall on a normal ship satisfies that, including the internal ones: a hull
     * split in two by a bulkhead has two rooms, both touching the skin, and the
     * bulkhead belongs to both. So the dormancy this field exists to describe
     * never once triggered.
     *
     * The question is whether the wall *is* the outside, which is `hullLines`.
     */
    const active = spec.mounting === 'edge' ? Boolean(mountedOn) && skin.has(mountedOn.id) : true;
    return {
        ok: faults.length === 0,
        faults: [...new Set(faults)],
        compartmentId: home?.id ?? null,
        mountedOn: mountedOn?.id ?? null,
        active,
        blockedBy,
    };
}
/**
 * Which line an edge component is bolted to, if any.
 *
 * A part that mounts on a wall lives *on* it rather than inside a room, so the
 * question is which line it is against rather than which compartment holds it.
 * Nearest wins, within `MOUNT_TOLERANCE`.
 *
 * Shared by the editor's placement check and by `activeComponents`, so the two
 * cannot come to different conclusions about what a thruster is attached to.
 */
export function mountLineFor(candidate, graph) {
    let nearest = MOUNT_TOLERANCE;
    let mounted = null;
    for (const line of graph.lines) {
        const segment = segmentOf(line, graph);
        if (!segment)
            continue;
        const distance = distanceToSegment(candidate.position, segment[0], segment[1]);
        if (distance > nearest)
            continue;
        nearest = distance;
        mounted = line;
    }
    return mounted;
}
/**
 * The components on this hull that are actually doing anything.
 *
 * Internal parts always count — a seat is a seat wherever it is bolted. An edge
 * part counts only where it can reach space: on the outer skin, and not on an
 * internal bulkhead or a wall that has since been shot away.
 *
 * ── Why this exists rather than a boolean on the component ──────────────────
 *
 * Activity is a fact about the *hull as it currently stands*, not about the
 * part. Draw a wall around a thruster and it goes dormant without anybody
 * touching the thruster; blow that wall off and it wakes. Storing it would mean
 * keeping it in step with every edit and every dent, and the first missed
 * update is a ship whose speed does not match its shape.
 *
 * Feed this to `computeShipPerformance` rather than the whole component list.
 * The editor and the flight loop must both do so, or the yard teaches a rule the
 * game does not enforce — which is precisely what happened for as long as
 * nothing called this.
 */
export function activeComponents(graph) {
    const skin = hullLines(graph);
    return graph.components.filter((component) => {
        if (COMPONENT_SPECS[component.type].mounting !== 'edge')
            return true;
        const mounted = mountLineFor(component, graph);
        return mounted !== null && skin.has(mounted.id);
    });
}
/** The same, as the type list `computeShipPerformance` asks for. */
export function activeComponentTypes(graph) {
    return activeComponents(graph).map((component) => component.type);
}
//# sourceMappingURL=placement.js.map